/**
 * Inline mentorship inquiry form.
 *
 * Rendered inside an expanded mentor card on Fellows Corner. Wraps the shared
 * mentorship form hook and fields in a compact layout. On success it redirects
 * to the inbox — matching the other inquiry flows — while the confirmation
 * itself is surfaced as a toast by the form hook.
 */

import { useRouter } from 'expo-router';
import { Clock } from 'lucide-react-native';
import { useEffect } from 'react';

import { MentorshipAnonymousCheckbox } from '@/components/fellows-corner/form/MentorshipAnonymousCheckbox';
import { MentorshipConsentCheckbox } from '@/components/fellows-corner/form/MentorshipConsentCheckbox';
import { MentorshipQuestionField } from '@/components/fellows-corner/form/MentorshipQuestionField';
import { Box } from '@/components/ui/box';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getFellowMentor, type MentorTarget } from '@/constants/fellowsCorner';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useMentorshipInquiryForm } from '@/hooks/useMentorshipInquiryForm';

/**
 * Away notice for the mentor being written to.
 *
 * A contextual banner rather than a line of muted text: it is the only warning
 * that a reply will be slow, and it has to survive a form full of controls. The
 * amber field matches the away treatment used elsewhere, and it sits at the top
 * of the form so it is read before the question is written rather than after.
 *
 * A clock, not the away banner's bell-off: that icon means an expert is not
 * being sent inquiries, which is true where it is used and false here, since an
 * away mentor still receives the question and replies on return.
 */
function AwayNotice({ mentorName }: { mentorName: string }) {
  return (
    <Box className="rounded-lg bg-background-warning px-4 py-3">
      <HStack className="items-start gap-2">
        <Icon as={Clock} size="sm" className="shrink-0 text-warning-800" />

        <Text size="sm" className="min-w-0 flex-1 text-warning-800">
          {mentorName} is away but will reply as soon as able. You&apos;re welcome to send your
          question now.
        </Text>
      </HStack>
    </Box>
  );
}

type MentorshipInquiryFormProps = {
  mentor: MentorTarget;
  /**
   * Whether the mentor is currently away. Surfaces a notice at the top of the
   * form and shifts the success copy, but never blocks submission: an away
   * mentor still receives the question and replies on return.
   */
  isAway?: boolean;
  /** When provided, renders a Cancel action. Omit when there's nothing to dismiss to. */
  onCancel?: () => void;
  /** Message to prefill, e.g. when the card is opened from a prompt chip. */
  initialMessage?: string;
  /** Opens the mentorship guidelines. When provided, the question field shows a link to it. */
  onOpenGuidelines?: () => void;
};

export function MentorshipInquiryForm({
  mentor,
  isAway,
  onCancel,
  initialMessage,
  onOpenGuidelines,
}: MentorshipInquiryFormProps) {
  const { colors } = useColors();

  const router = useRouter();

  const isMaintenanceMode = useMaintenanceMode();

  const { control, errors, setValue, submitInquiry, isSubmitting, submissionStatus, canSubmit } =
    useMentorshipInquiryForm({ mentor, isAway, onSuccess: () => router.replace(ROUTES.INBOX) });

  useEffect(() => {
    setValue('message', initialMessage ?? '', { shouldValidate: !!initialMessage });
  }, [initialMessage, setValue]);

  const isUnavailable = mentor.recipientExpertId === null;

  if (isUnavailable) {
    return (
      <Text size="sm" className="text-typography-600">
        {mentor.name} isn&apos;t accepting questions through Primum yet. Please check back soon.
      </Text>
    );
  }

  return (
    <VStack space="lg">
      {isAway ? <AwayNotice mentorName={mentor.name} /> : null}

      <MentorshipQuestionField
        control={control}
        errors={errors}
        isDisabled={isSubmitting}
        placeholder={getFellowMentor(mentor.id)?.questionPlaceholder}
        onOpenGuidelines={onOpenGuidelines}
      />

      <MentorshipAnonymousCheckbox control={control} isDisabled={isSubmitting} />

      <MentorshipConsentCheckbox control={control} errors={errors} isDisabled={isSubmitting} />

      <HStack className="items-center gap-3">
        {onCancel ? (
          <Button variant="link" onPress={onCancel} isDisabled={isSubmitting}>
            <ButtonText>Cancel</ButtonText>
          </Button>
        ) : null}

        <Button
          onPress={submitInquiry}
          isDisabled={isMaintenanceMode || !canSubmit() || isSubmitting}
          className="gap-x-2">
          {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}
          <ButtonText>{isSubmitting ? submissionStatus || 'Submitting...' : 'Submit'}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
